import React, { useState } from 'react';
import { ArrowRight, TrendingUp, BookOpen, Sprout, HeartPulse, Lightbulb } from 'lucide-react';
import { useCMS } from '../context/CMSContext';

const Home = () => {
  const [selectedCard, setSelectedCard] = useState(null);
  const { content, resolveImage, getPageData } = useCMS();
  const pageData = getPageData('/');

  const c = (key, fallback) => {
    if (pageData && pageData.sectionData) {
      // Map common section keys to section types
      const mappings = {
        'hero': 'hero',
        'philosophy': 'philosophy',
        'cta': 'cta',
        'stats': 'stats',
        'framework': 'framework',
        'flagships': 'flagships',
        'partners': 'partners'
      };
      
      for (const [prefix, type] of Object.entries(mappings)) {
        if (key.includes(`_${prefix}_`)) {
          const field = key.split(`_${prefix}_`)[1];
          const val = pageData.sectionData[type]?.[field];
          if (val) return val;
        }
      }
    }
    return content[key] || fallback;
  };

  const partners = [
  {
    name: "Shine Leadership Int",
    logo: "/images/shineleadership.jpeg",
  },
  {
    name: "Ministry of Education and Sports",
    logo: "/images/educationsports.png",
  },
  {
    name: "CMHLIVE",
    logo: "/images/CMHLIVE.jpeg",
  },
  {
    name: "Cinelab Akademie",
    logo: "/images/cinelab.jpeg",
  },
  {
    name: "Ministry of Gender,Labour and Social Development",
    logo: "/images/image.png",
  },
];

  return (
    <div className="animate-in fade-in duration-1000">
      
      {/* 1. Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute top-0 right-0 w-2/3 h-full bg-brandBeige/50 -skew-x-6 transform translate-x-1/4 z-0"></div>
        <div className="absolute top-20 left-10 w-64 h-64 bg-brandGreen/5 organic-shape animate-slow-spin"></div>
        <div className="absolute bottom-40 right-20 w-96 h-96 bg-brandMauve/10 organic-shape animate-pulse"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-6 space-y-8">
              <div className="inline-flex items-center space-x-3 px-4 py-2 bg-brandWhite rounded-full shadow-sm border border-brandGreen/10">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brandGreen opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-brandGreen"></span>
                </span>
                <span className="text-xs font-bold uppercase tracking-widest text-brandBlack/60">{c('home_hero_badge', 'Based in Uganda, Serving Africa')}</span>
              </div>
              
              <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold text-brandBlack leading-tight md:leading-[1.1] tracking-tight">Transforming Lives Together</h1>
              
              <p className="text-lg sm:text-xl text-brandBlack/60 leading-relaxed max-w-lg">
                Eunikare International is a community impact organisation dedicated to advancing education, economic empowerment, and sustainable development.
              </p>
              
              <div className="flex flex-wrap gap-4 pt-4">
                <a className="bg-brandBlack text-brandWhite px-8 py-4 sm:px-10 sm:py-5 rounded-full font-bold hover:bg-brandGreen transition-all shadow-2xl flex items-center group text-sm sm:text-base" href="#/partnership">
                  Partner With Us
                  <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-2 transition-transform" />
                </a>
                <a className="bg-brandWhite text-brandBlack border border-brandBlack/5 px-8 py-4 sm:px-10 sm:py-5 rounded-full font-bold hover:bg-brandBeige transition-all flex items-center text-sm sm:text-base" href="#/about">
                  Learn Our Story
                </a>
              </div>
            </div>
            
            <div className="lg:col-span-6 relative">
              <div className="relative z-10 p-4">
                <div className="rounded-[4rem_1rem_4rem_1rem] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.2)] aspect-[4/5] relative">
                  <img className="w-full h-full object-cover" alt="Community Impact" src={resolveImage('home_hero_bg', '/images/smiling_kids.jpeg')} />
                  <div className="absolute inset-0 bg-gradient-to-t from-brandBlack/40 to-transparent"></div>
                </div>
                
                <div className="absolute -bottom-6 -left-4 sm:-bottom-10 sm:-left-10 bg-brandWhite p-6 sm:p-8 rounded-[1.5rem] sm:rounded-[2rem] shadow-2xl max-w-[200px] sm:max-w-[240px] animate-float border border-brandBeige">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-brandGreen rounded-xl flex items-center justify-center text-brandWhite shrink-0">
                      <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div>
                      <p className="text-xl sm:text-2xl font-bold">10</p>
                      <p className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold text-brandBlack/40">Years of Impact</p>
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-brandBlack/60 leading-snug">Continuous dedication to vulnerable children and rural empowerment.</p>
                </div>
              </div>
              <div className="absolute -top-10 -right-10 w-40 h-40 dot-pattern opacity-30 -z-10"></div>
            </div>

          </div>
        </div>
      </section>

      {/* 2. Impact Stats */}
      <section className="py-20 bg-brandBlack text-brandWhite overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-full dot-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            <div className="space-y-2">
              <h3 className="text-4xl md:text-6xl font-bold text-brandGreen tabular-nums">{c('stat_1_num', '1,200+')}</h3>
              <p className="text-xs uppercase tracking-[0.3em] font-bold text-brandWhite/40">{c('stat_1_label', 'Children Educated')}</p>
            </div>
            <div className="space-y-2">
              <h3 className="text-4xl md:text-6xl font-bold text-brandGreen tabular-nums">{c('stat_2_num', '600+')}</h3>
              <p className="text-xs uppercase tracking-[0.3em] font-bold text-brandWhite/40">{c('stat_2_label', 'Women Empowered')}</p>
            </div>
            <div className="space-y-2">
              <h3 className="text-4xl md:text-6xl font-bold text-brandGreen tabular-nums">{c('stat_3_num', '15')}</h3>
              <p className="text-xs uppercase tracking-[0.3em] font-bold text-brandWhite/40">{c('stat_3_label', 'Communities Served')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Strategic Framework */}
      <section className="py-32 bg-brandWhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="text-sm font-bold uppercase tracking-[0.4em] text-brandGreen mb-6">Strategic Framework</h2>
            <h3 className="text-5xl font-bold leading-tight" dangerouslySetInnerHTML={{__html: c('home_framework_title', 'Holistic solutions for <br><span class="text-brandMauve">multidimensional</span> needs.')}}></h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: 'Education + Skill Development',
                  subtitle: 'Expanding access to quality education through our schools.',
                  icon: <BookOpen className="w-5 h-5 text-brandBlack" />
                },
                {
                  title: 'Women in Agribusiness',
                  subtitle: 'Smart farming and financial inclusion via Agriscale Initiative.',
                  icon: <Sprout className="w-5 h-5 text-brandBlack" />
                },
                {
                  title: 'Advancing health & wellbeing',
                  subtitle: 'Advancing health & wellbeing of young people through Cycle of Care.',
                  icon: <HeartPulse className="w-5 h-5 text-brandBlack" />
                },
                {
                  title: 'Africa\'s Innovation Challenge',
                  subtitle: 'Equipping the next Africa\'s Innovators & Entrepreneurs at Kilimani.',
                  icon: <Lightbulb className="w-5 h-5 text-brandBlack" />
                }
              ].map((item) => {
                const isSelected = selectedCard === item.title;
                return (
                <button
                  key={item.title}
                  type="button"
                  onClick={() => setSelectedCard(item.title)}
                  className={`relative text-left rounded-[2.5rem] rounded-tr-[1.8rem] rounded-br-[1.8rem] border p-6 min-h-[240px] overflow-hidden transition-all duration-300 ease-out ${isSelected ? 'border-transparent bg-brandGreen text-white shadow-[0_30px_60px_-30px_rgba(16,185,129,0.75)]' : 'border-brandWhite bg-[#faf8f3] text-brandBlack hover:-translate-y-1 hover:shadow-lg'}`}
                >
                  <div className={`absolute top-5 left-5 w-12 h-12 rounded-[1.8rem] flex items-center justify-center shadow-xl ${isSelected ? 'bg-white/10 text-white' : 'bg-brandWhite text-brandBlack'}`}>
                    {React.cloneElement(item.icon, { className: `w-5 h-5 ${isSelected ? 'text-white' : 'text-brandBlack'}` })}
                  </div>
                  <div className="mt-14 space-y-3">
                    <div className="flex items-center justify-between gap-4">
                      <h4 className={`text-xl font-bold leading-snug ${isSelected ? 'text-white' : 'text-brandBlack'}`}>{item.title}</h4>
                    </div>
                    <p className={`text-sm leading-6 ${isSelected ? 'text-white/80' : 'text-brandBlack/70'}`}>{item.subtitle}</p>
                  </div>
                  {isSelected && (
                    <span className="absolute bottom-5 left-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white shadow-lg shadow-brandBlack/10">
                      <ArrowRight className="w-4 h-4 rotate-180" />
                    </span>
                  )}
                </button>
              )})}
          </div>
        </div>
      </section>

      {/* 4. Philosophy */}
      <section className="py-32 bg-brandBeige/30 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            
            <div className="relative">
              <div className="relative z-10 rounded-[4rem] overflow-hidden shadow-2xl">
                <img className="w-full aspect-square object-cover" alt="Focus" src={resolveImage('philosophy_bg', '/images/WhatsApp Image 2026-06-20 at 16.17.09.jpeg')} />
              </div>
              <div className="absolute -top-10 -left-10 w-64 h-64 bg-brandGreen/10 organic-shape -z-10 animate-slow-spin"></div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 dot-pattern opacity-20 -z-10"></div>
            </div>
            
            <div className="space-y-10">
              <h2 className="text-sm font-bold uppercase tracking-[0.4em] text-brandMauve">Our Philosophy</h2>
              <h3 className="text-5xl font-bold leading-[1.1]" dangerouslySetInnerHTML={{__html: c('philosophy_title', 'Sustainability is not an <br><span class="text-brandGreen italic">accident</span>, it is designed.')}}></h3>
              <p className="text-lg text-brandBlack/60 leading-relaxed">
                {c('philosophy_subtitle', 'We move beyond traditional aid. By focusing on entrepreneurship and vocational skills alongside basic needs, we ensure that every community we touch becomes an engine of its own growth.')}
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="flex space-x-4">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-brandGreen/10 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-brandGreen"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path></svg>
                  </div>
                  <div>
                    <h5 className="font-bold mb-1">Accountability</h5>
                    <p className="text-sm text-brandBlack/50">Transparency, integrity, and responsible leadership.</p>
                  </div>
                </div>
                <div className="flex space-x-4">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-brandMauve/20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-brandMauve"><path d="M11 12h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 14"></path><path d="m7 18 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9"></path><path d="m2 13 6 6"></path></svg>
                  </div>
                  <div>
                    <h5 className="font-bold mb-1">{c('home_philosophy_point2_title', 'Locally Led')}</h5>
                    <p className="text-sm text-brandBlack/50">{c('home_philosophy_point2_desc', 'Programs designed by those who live the reality.')}</p>
                  </div>
                </div>
              </div>
              
              <div className="pt-6">
                <a className="inline-flex items-center space-x-4 font-bold text-lg group" href="#/impact">
                  <span className="underline underline-offset-8 decoration-2 decoration-brandGreen">Read Our Impact Report</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 5. Flagships Content */}
      <section className="py-24 bg-brandWhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">

            {/* Mikka Parents School */}
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-[2rem] mb-8 aspect-video">
                <img
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                  alt="Mikka Parents School"
                  src="/images/school.png"
                />
                <div className="absolute top-6 left-6 w-14 h-14 bg-brandWhite/90 backdrop-blur rounded-2xl flex items-center justify-center text-3xl shadow-xl border border-white/50">
                  🎓
                </div>
              </div>

              <h4 className="text-2xl font-bold mb-4 group-hover:text-brandGreen transition-colors">
                Mikka Parents School
              </h4>

              <p className="text-brandBlack/60 text-sm leading-relaxed mb-6 line-clamp-2">
                Championing Education and Skilling for every child we support
              </p>

              <div className="flex items-center space-x-2 text-brandGreen font-bold text-sm">
                <span>View Details</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>

            {/* Agriscale Initiative */}
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-[2rem] mb-8 aspect-video">
                <img
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                  alt="Agriscale Initiative"
                  src="/images/Agris.png"
                />
                <div className="absolute top-6 left-6 w-14 h-14 bg-brandWhite/90 backdrop-blur rounded-2xl flex items-center justify-center text-3xl shadow-xl border border-white/50">
                  🌱
                </div>
              </div>

              <h4 className="text-2xl font-bold mb-4 group-hover:text-brandGreen transition-colors">
                Agriscale Initiative
              </h4>

              <p className="text-brandBlack/60 text-sm leading-relaxed mb-6 line-clamp-2">
                Empowering women and girls to grow sustainable livelihoods
              </p>

              <div className="flex items-center space-x-2 text-brandGreen font-bold text-sm">
                <span>View Details</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>

            {/* Collectively Christians */}
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-[2rem] mb-8 aspect-video">
                <img
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                  alt="CollectivelyChristians.com"
                  src="/images/christian.png"
                />
                <div className="absolute top-6 left-6 w-14 h-14 bg-brandWhite/90 backdrop-blur rounded-2xl flex items-center justify-center text-3xl shadow-xl border border-white/50">
                  ✝️
                </div>
              </div>

              <h4 className="text-2xl font-bold mb-4 group-hover:text-brandGreen transition-colors">
                CollectivelyChristians.com
              </h4>

              <p className="text-brandBlack/60 text-sm leading-relaxed mb-6 line-clamp-2">
                Building empowered, self-reliant Christian communities
              </p>

              <div className="flex items-center space-x-2 text-brandGreen font-bold text-sm">
                <span>View Details</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>

          </div>
        </div>
      </section>

     {/* 6. Partners */}
<section className="py-24 bg-brandBeige/20 border-t border-brandBeige">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

    {/* Changed to text-lg on mobile, text-xl on desktop, and boosted color contrast to 80% */}
    <p className="text-center text-lg md:text-xl font-bold uppercase tracking-[0.2em] text-brandBlack/80 mb-12">
      Collaborating With Global Change-Makers
    </p>

    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 items-center">
      {partners.map((partner) => (
        <div
          key={partner.name}
          className="flex items-center justify-center bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300"
        >
          <img
            src={partner.logo}
            alt={partner.name}
            className="h-16 md:h-20 w-auto object-contain"
          />
        </div>
      ))}
    </div>

  </div>
</section>


      {/* 7. CTA Element */}
      <section className="p-4 md:p-8 bg-brandWhite">
        <div className="max-w-7xl mx-auto bg-brandGreen rounded-[4rem] overflow-hidden relative min-h-[500px] flex items-center">
          <div className="absolute inset-0 dot-pattern opacity-10"></div>
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-brandBlack/5 organic-shape"></div>
          <div className="relative z-10 w-full px-8 md:px-20 text-brandWhite py-20">
            <div className="max-w-3xl space-y-8">
              <h2 className="text-5xl md:text-7xl font-bold leading-tight" dangerouslySetInnerHTML={{__html: c('cta_title', 'Ready to make a <br><span class="text-brandBlack">tangible</span> difference?')}}></h2>
              <p className="text-xl text-brandWhite/80 max-w-xl leading-relaxed">
                {c('cta_subtitle', "Whether you're an individual, a corporate partner, or an institution, there is a place for you in our mission. Let's co-create a future where every life has equal value.")}
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <a className="bg-brandWhite text-brandGreen px-12 py-5 rounded-full font-bold hover:bg-brandBlack hover:text-brandWhite transition-all shadow-xl" href="#/partnership">
                  Become a Partner
                </a>
                <a className="bg-brandBlack text-brandWhite px-12 py-5 rounded-full font-bold hover:bg-brandWhite hover:text-brandBlack transition-all shadow-xl" href="#/impact">
                  Support Our Work
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      
    </div>
  );
};

export default Home;
